import { initCloudflareBanner } from "./components/cloudflare-banner";
import { mountSiteChrome } from "./components/site-chrome";
import { controllerForFamily, createController } from "./components/controller";
import { classicImages } from "./classic-images";
import { AudioController } from "./core/audio";
import { CanvasSurface, toggleFullscreen } from "./core/canvas";
import { GameLoop } from "./core/game-loop";
import { InputController } from "./core/input";
import { saveBestScore } from "./core/storage";
import type { GameHud, InputAction } from "./core/types";
import { cardImages } from "./card-images";
import { drawPanel, setText } from "./games/drawing";
import { getGameFactory } from "./games";
import {
  gameCatalog,
  type EngineFamily,
  type GameDefinition,
} from "./shared/catalog";
import {
  classicsCatalog,
  isClassicPlayable,
  type ClassicDefinition,
} from "./shared/classics-catalog";

initCloudflareBanner();
mountSiteChrome();
showDeliveryTagline();

/**
 * Floatbar tagline: how long Cloudflare took to start a Worker, build this page,
 * and deliver it to this visitor. Measured from the browser's own navigation
 * timing — the window from the request leaving the browser to the last byte of
 * the response arriving (responseEnd - requestStart) is the real
 * start-build-and-deliver time for this page, served by the flarecade-website
 * Worker. No estimate: if the timing API is unavailable, the element shows a
 * dash rather than a fabricated number.
 */
function showDeliveryTagline(): void {
  const target = document.getElementById("fb-deliver-ms");
  if (!target) return;

  const paint = (): void => {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    let ms: number | null = null;
    if (nav && nav.responseEnd > 0 && nav.requestStart > 0) {
      ms = nav.responseEnd - nav.requestStart;
    } else if (nav && nav.responseEnd > 0) {
      ms = nav.responseEnd - nav.startTime;
    }
    target.textContent =
      ms !== null && Number.isFinite(ms) && ms >= 0
        ? `${ms < 10 ? ms.toFixed(1) : Math.round(ms)} ms`
        : "—";
  };

  if (document.readyState === "complete") {
    paint();
  } else {
    window.addEventListener("load", () => window.setTimeout(paint, 0), { once: true });
  }
}

function getElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing required element: ${id}`);
  return element as T;
}

function createTextElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className: string,
  text: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tagName);
  element.className = className;
  element.textContent = text;
  return element;
}

const familyNames: Record<EngineFamily, string> = {
  grid: "Grid engine",
  combat: "Combat engine",
  motion: "Motion engine",
};

const familyColors: Record<EngineFamily, string> = {
  grid: "#42e2c3",
  combat: "#ff5d5d",
  motion: "#2fe3ff",
};

const grid = getElement<HTMLDivElement>("game-grid");
const dialog = getElement<HTMLDialogElement>("game-dialog");
const stageShell = getElement<HTMLDivElement>("stage-shell");
const stageFamily = getElement<HTMLSpanElement>("stage-family");
const stageTitle = getElement<HTMLHeadingElement>("stage-title");
const stageProduct = getElement<HTMLParagraphElement>("stage-product");
const stageScore = getElement<HTMLElement>("stage-score");
const stageTime = getElement<HTMLElement>("stage-time");
const pauseButton = getElement<HTMLButtonElement>("pause-game");
const muteButton = getElement<HTMLButtonElement>("mute-game");
const fullscreenButton = getElement<HTMLButtonElement>("fullscreen-game");
const closeButton = getElement<HTMLButtonElement>("close-game");
const canvasWrapElement = document.querySelector<HTMLDivElement>(".canvas-wrap");
const canvas = getElement<HTMLCanvasElement>("game-canvas");
const startPanel = getElement<HTMLDivElement>("start-panel");
const startFamily = getElement<HTMLParagraphElement>("start-family");
const startTitle = getElement<HTMLHeadingElement>("start-title");
const startSummary = getElement<HTMLParagraphElement>("start-summary");
const startControls = getElement<HTMLParagraphElement>("start-controls");
const startButton = getElement<HTMLButtonElement>("start-game");
const stageHelp = getElement<HTMLParagraphElement>("stage-help");
const liveRegion = getElement<HTMLDivElement>("live-region");

if (!canvasWrapElement) throw new Error("Missing required canvas wrapper");
const canvasWrap = canvasWrapElement;

const surface = new CanvasSurface(canvas);
const input = new InputController(canvas);
const audio = new AudioController();
let selectedGame: GameDefinition | null = null;
let roundActive = false;
let sessionGeneration = 0;
let lastScore = -1;
let lastTime = -1;
let lastMessage = "";

function announce(message: string): void {
  liveRegion.textContent = message;
}

function updateHud(hud: GameHud): void {
  const time = Math.max(0, Math.ceil(hud.time));
  if (hud.score !== lastScore) {
    stageScore.textContent = String(hud.score);
    lastScore = hud.score;
  }
  if (time !== lastTime) {
    stageTime.textContent = String(time);
    lastTime = time;
  }
  if (hud.message !== lastMessage) {
    stageHelp.textContent = hud.message;
    lastMessage = hud.message;
  }
}

function finishRound(hud: GameHud): void {
  if (!selectedGame) return;
  roundActive = false;
  input.setEnabled(false);
  canvasWrap.classList.remove("is-running");
  pauseButton.disabled = true;
  pauseButton.textContent = "Pause";
  pauseButton.setAttribute("aria-pressed", "false");
  const best = saveBestScore(selectedGame.id, hud.score);
  startFamily.textContent = "ROUND COMPLETE";
  startTitle.textContent = hud.message;
  startSummary.textContent = `Score ${hud.score.toLocaleString()} // Guest best ${best.toLocaleString()}`;
  startControls.textContent = "Scores stay in this browser. No account or personal data is used.";
  startButton.textContent = "Play again";
  startButton.disabled = false;
  startPanel.hidden = false;
  announce(`${hud.message}. Score ${hud.score}. Best score ${best}.`);
}

const loop = new GameLoop(surface, input, updateHud, finishRound);

function renderAttractScreen(definition: GameDefinition): void {
  const context = surface.prepare("#071218");
  const accent = familyColors[definition.family];
  const gradient = context.createLinearGradient(0, 0, 1280, 720);
  gradient.addColorStop(0, "#071218");
  gradient.addColorStop(0.62, "#10273a");
  gradient.addColorStop(1, "#171333");
  context.fillStyle = gradient;
  context.fillRect(0, 0, 1280, 720);

  context.strokeStyle = `${accent}33`;
  context.lineWidth = 2;
  for (let x = -400; x < 1500; x += 92) {
    context.beginPath();
    context.moveTo(640, 270);
    context.lineTo(x, 720);
    context.stroke();
  }
  drawPanel(context, 250, 210, 780, 300, accent);
  setText(context, 14, 850, accent, "center");
  context.fillText(familyNames[definition.family].toUpperCase(), 640, 270);
  setText(context, 58, 900, "#f7fbff", "center");
  context.fillText(definition.title, 640, 338);
  setText(context, 20, 750, "#a9bbc4", "center");
  context.fillText(definition.product.toUpperCase(), 640, 393);
  setText(context, 16, 700, "#9feddd", "center");
  context.fillText("READY FOR SIGNAL", 640, 462);
}

function openGame(definition: GameDefinition): void {
  if (!definition.playable) return;
  sessionGeneration += 1;
  loop.stop();
  input.setEnabled(false);
  roundActive = false;
  selectedGame = definition;
  stageShell.dataset.family = definition.family;
  stageFamily.textContent = definition.family.toUpperCase();
  stageTitle.textContent = definition.title;
  stageProduct.textContent = definition.product;
  stageScore.textContent = "0";
  stageTime.textContent = String(ROUND_DISPLAY_SECONDS);
  startFamily.textContent = familyNames[definition.family].toUpperCase();
  startTitle.textContent = definition.title;
  startSummary.textContent = definition.summary;
  startControls.textContent = definition.controls;
  startButton.textContent = "Start round";
  startButton.disabled = false;
  startPanel.hidden = false;
  stageHelp.textContent = "Use keyboard, pointer, or the on-screen controls.";
  canvas.setAttribute("aria-label", `${definition.title}. ${definition.controls}`);
  canvasWrap.classList.remove("is-running");
  pauseButton.disabled = true;
  pauseButton.textContent = "Pause";
  pauseButton.setAttribute("aria-pressed", "false");
  lastScore = -1;
  lastTime = -1;
  lastMessage = "";
  renderAttractScreen(definition);
  if (!dialog.open) dialog.showModal();
  startButton.focus();
}

const ROUND_DISPLAY_SECONDS = 60;

async function startRound(): Promise<void> {
  if (!selectedGame || startButton.disabled) return;
  const game = selectedGame;
  const generation = sessionGeneration;
  const factory = getGameFactory(game.id);
  if (!factory) {
    announce("This game is not available in the current preview.");
    return;
  }

  startButton.disabled = true;
  let soundUnavailable = false;
  try {
    await audio.unlock();
  } catch {
    soundUnavailable = true;
  }

  if (generation !== sessionGeneration || selectedGame?.id !== game.id || !dialog.open) return;

  if (soundUnavailable) {
    audio.setMuted(true);
    muteButton.textContent = "Sound unavailable";
    muteButton.setAttribute("aria-pressed", "true");
    announce("Sound is unavailable. The round will continue muted.");
  }

  lastScore = -1;
  lastTime = -1;
  lastMessage = "";
  stageScore.textContent = "0";
  stageTime.textContent = String(ROUND_DISPLAY_SECONDS);
  startPanel.hidden = true;
  canvasWrap.classList.add("is-running");
  pauseButton.disabled = false;
  pauseButton.textContent = "Pause";
  pauseButton.setAttribute("aria-pressed", "false");
  input.setEnabled(true);
  roundActive = true;
  loop.start(
    factory({
      input,
      audio,
      announce,
      reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    }),
  );
  canvas.focus();
  announce(`${game.title} started. ${game.controls}`);
}

function closeRound(): void {
  sessionGeneration += 1;
  roundActive = false;
  loop.stop();
  input.setEnabled(false);
  canvasWrap.classList.remove("is-running");
  if (document.fullscreenElement) {
    void document.exitFullscreen().catch(() => undefined);
  }
  if (dialog.open) dialog.close();
}

function createGameCard(definition: GameDefinition): HTMLElement {
  const card = document.createElement("article");
  card.className = "game-card";
  card.dataset.family = definition.family;

  const imageUrl = cardImages[definition.id];
  if (imageUrl) {
    const image = document.createElement("img");
    image.className = "card-image";
    image.src = imageUrl;
    image.alt = `${definition.title} concept artwork`;
    image.loading = "lazy";
    image.decoding = "async";
    card.appendChild(image);
  } else {
    const placeholder = document.createElement("div");
    placeholder.className = "card-placeholder";
    placeholder.setAttribute("aria-hidden", "true");
    card.appendChild(placeholder);
  }

  if (!definition.playable) {
    card.classList.add("is-soon");
    const overlay = document.createElement("div");
    overlay.className = "soon-overlay";
    const badge = document.createElement("div");
    badge.className = "soon-badge";
    badge.appendChild(createTextElement("span", "soon-badge-title", "Coming Soon"));
    badge.appendChild(
      createTextElement("span", "soon-badge-sub", "Developed, Deployed & Delivered on Cloudflare"),
    );
    overlay.appendChild(badge);
    card.appendChild(overlay);
  }

  const topLine = document.createElement("div");
  topLine.className = "card-topline";
  topLine.appendChild(createTextElement("span", "family-tag", definition.family));
  topLine.appendChild(
    createTextElement(
      "span",
      `availability-tag${definition.playable ? " is-live" : ""}`,
      definition.playable ? "Playable" : "Coming soon",
    ),
  );

  const content = document.createElement("div");
  content.className = "card-content";
  content.appendChild(createTextElement("h3", "", definition.title));
  content.appendChild(createTextElement("p", "card-product", definition.product));
  content.appendChild(createTextElement("p", "card-summary", definition.summary));

  if (definition.playable) {
    const action = document.createElement("button");
    action.className = "card-action";
    action.type = "button";
    action.textContent = "Play now →";
    action.addEventListener("click", () => openGame(definition));
    content.appendChild(action);
  } else {
    // Preview the control style this title will ship with. Desktop-only;
    // hidden on touch via the component's CSS.
    content.appendChild(createController(controllerForFamily(definition.family)));
  }
  card.appendChild(topLine);
  card.appendChild(content);
  return card;
}

const fragment = document.createDocumentFragment();
for (const definition of gameCatalog) fragment.appendChild(createGameCard(definition));
grid.appendChild(fragment);

/**
 * Display name for an arcade classic: the actual game we deployed, followed by
 * the classic it is based on in parentheses — e.g. "Freedoom (Doom)",
 * "Paddle Duel (Pong)". Titles without a `cloneOf` (e.g. Hextris, 2048) render
 * their name alone.
 */
function classicDisplayName(definition: ClassicDefinition): string {
  return definition.cloneOf
    ? `${definition.title} (${definition.cloneOf})`
    : definition.title;
}

function createClassicCard(definition: ClassicDefinition): HTMLElement {
  const playable = isClassicPlayable(definition);
  const card = document.createElement("article");
  card.className = `classic-card${playable ? " is-playable" : ""}`;
  card.style.setProperty("--c", definition.accent);

  const coverUrl = classicImages[definition.id];
  if (coverUrl) {
    card.classList.add("has-cover");
    const cover = document.createElement("img");
    cover.className = "classic-cover";
    cover.src = coverUrl;
    cover.alt = `${classicDisplayName(definition)} cover art`;
    cover.loading = "lazy";
    cover.decoding = "async";
    card.appendChild(cover);
  }

  if (playable) {
    const pill = createTextElement("span", "pill free", "Free · Play now");
    card.appendChild(pill);
  } else {
    card.classList.add("is-soon");
    const overlay = document.createElement("div");
    overlay.className = "soon-overlay";
    const badge = document.createElement("div");
    badge.className = "soon-badge";
    badge.appendChild(createTextElement("span", "soon-badge-title", "Coming Soon"));
    badge.appendChild(
      createTextElement("span", "soon-badge-sub", "Developed, Deployed & Delivered on Cloudflare"),
    );
    overlay.appendChild(badge);
    card.appendChild(overlay);
  }

  card.appendChild(createTextElement("span", "fam", `${definition.genre} · ${definition.year}`));
  card.appendChild(createTextElement("h4", "", classicDisplayName(definition)));
  card.appendChild(createTextElement("p", "", definition.license));

  if (playable && definition.href) {
    const href = definition.href;
    card.setAttribute("role", "link");
    card.tabIndex = 0;
    card.addEventListener("click", () => {
      window.location.href = href;
    });
    card.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        window.location.href = href;
      }
    });
  }
  return card;
}

const classicsGrid = document.getElementById("classics-grid");
if (classicsGrid) {
  const classicsFragment = document.createDocumentFragment();
  for (const definition of classicsCatalog) {
    classicsFragment.appendChild(createClassicCard(definition));
  }
  classicsGrid.appendChild(classicsFragment);
}

// Hero game count = every title on the site (originals + classics), kept in sync
// with the catalogs so it never drifts from what is actually shown.
const totalGames = gameCatalog.length + classicsCatalog.length;
const heroCount = document.querySelector<HTMLElement>(".edge-core b");
if (heroCount) heroCount.textContent = String(totalGames);

// Hero background collage: every game's cover art, most-popular first. The
// classics catalog is already ordered by franchise popularity; the originals
// (the product stars) lead. Covers tile the hero behind a scrim.
const heroCollage = document.getElementById("hero-collage");
if (heroCollage) {
  const covers: Array<{ src: string; alt: string }> = [];
  for (const game of gameCatalog) {
    const src = cardImages[game.id];
    if (src) covers.push({ src, alt: `${game.title} cover art` });
  }
  for (const classic of classicsCatalog) {
    const src = classicImages[classic.id];
    if (src) covers.push({ src, alt: `${classicDisplayName(classic)} cover art` });
  }
  const collageFragment = document.createDocumentFragment();
  for (const cover of covers) {
    const tile = document.createElement("img");
    tile.className = "hero-collage-tile";
    tile.src = cover.src;
    tile.alt = "";
    tile.setAttribute("aria-hidden", "true");
    tile.loading = "lazy";
    tile.decoding = "async";
    collageFragment.appendChild(tile);
  }
  heroCollage.appendChild(collageFragment);
}

// Scroll-driven page theming: page morphs to the section in view.
const themedSections = document.querySelectorAll<HTMLElement>("[data-theme-a]");
if (themedSections.length > 0 && typeof IntersectionObserver !== "undefined") {
  const rootStyle = document.documentElement.style;
  const themeObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > 0.4) {
          const a = entry.target.getAttribute("data-theme-a");
          const b = entry.target.getAttribute("data-theme-b");
          if (a) rootStyle.setProperty("--theme-a", a);
          if (b) rootStyle.setProperty("--theme-b", b);
        }
      }
    },
    { threshold: [0.4, 0.6] },
  );
  for (const section of themedSections) themeObserver.observe(section);
}

// Cloud cost calculator.
const calcPopover = document.getElementById("calc");
const calcToggle = document.getElementById("calc-toggle");
const calcClose = document.getElementById("calc-close");
const segProvider = document.getElementById("seg-provider");
const segTier = document.getElementById("seg-tier");
const calcSave = document.getElementById("calc-save");
const calcLbl = document.getElementById("calc-lbl");
if (calcPopover && calcToggle && calcClose && segProvider && segTier && calcSave && calcLbl) {
  type Provider = "aws" | "azure" | "gcp";
  type Tier = "single" | "multi" | "cf";
  // Representative monthly egress cost for 10 TB/mo. Flarecade (R2) egress = $0.
  const model: Record<Provider, { name: string; single: number; multi: number; cf: number }> = {
    aws: { name: "AWS S3", single: 900, multi: 1740, cf: 9200 },
    azure: { name: "Azure Blob", single: 780, multi: 1560, cf: 8700 },
    gcp: { name: "Google Cloud Storage", single: 1150, multi: 2100, cf: 10400 },
  };
  let provider: Provider = "aws";
  let tier: Tier = "single";
  const tierLabel: Record<Tier, string> = {
    single: "single-region",
    multi: "multi-region",
    cf: "Cloudflare-equivalent (330+ locations)",
  };
  const renderCalc = (): void => {
    const value = model[provider][tier];
    calcSave.textContent = `$${value.toLocaleString()}`;
    calcLbl.textContent = `est. monthly ${tierLabel[tier]} egress on ${model[provider].name} — Flarecade pays $0`;
  };
  const setOpen = (open: boolean): void => {
    calcPopover.classList.toggle("open", open);
    calcToggle.setAttribute("aria-expanded", String(open));
  };
  calcToggle.addEventListener("click", () => setOpen(!calcPopover.classList.contains("open")));
  calcClose.addEventListener("click", () => setOpen(false));
  segProvider.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const p = target.dataset.p as Provider | undefined;
    if (!p) return;
    provider = p;
    for (const child of Array.from(segProvider.children)) child.classList.toggle("on", child === target);
    renderCalc();
  });
  segTier.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const t = target.dataset.t as Tier | undefined;
    if (!t) return;
    tier = t;
    for (const child of Array.from(segTier.children)) child.classList.toggle("on", child === target);
    renderCalc();
  });
  renderCalc();
}

const validActions = new Set<InputAction>([
  "left",
  "right",
  "up",
  "down",
  "primary",
  "secondary",
]);
for (const button of document.querySelectorAll<HTMLButtonElement>("[data-action]")) {
  const action = button.dataset.action;
  if (action && validActions.has(action as InputAction)) {
    input.bindVirtualButton(button, action as InputAction);
  }
}

startButton.addEventListener("click", () => {
  void startRound();
});

pauseButton.addEventListener("click", () => {
  if (!roundActive) return;
  const paused = !loop.isPaused();
  loop.setPaused(paused);
  input.setEnabled(!paused);
  pauseButton.textContent = paused ? "Resume" : "Pause";
  pauseButton.setAttribute("aria-pressed", String(paused));
  announce(paused ? "Round paused." : "Round resumed.");
  if (!paused) canvas.focus();
});

muteButton.addEventListener("click", () => {
  const muted = !audio.isMuted();
  audio.setMuted(muted);
  muteButton.textContent = muted ? "Sound off" : "Sound on";
  muteButton.setAttribute("aria-pressed", String(muted));
  if (!muted) audio.play("confirm");
});

fullscreenButton.addEventListener("click", () => {
  void toggleFullscreen(stageShell).catch(() => {
    announce("Fullscreen is unavailable in this browser.");
  });
});

document.addEventListener("fullscreenchange", () => {
  fullscreenButton.textContent = document.fullscreenElement ? "Exit fullscreen" : "Fullscreen";
});

closeButton.addEventListener("click", closeRound);
dialog.addEventListener("cancel", (event) => {
  event.preventDefault();
  closeRound();
});
dialog.addEventListener("close", () => {
  if (roundActive) closeRound();
});

document.addEventListener("visibilitychange", () => {
  if (!document.hidden || !roundActive || loop.isPaused()) return;
  loop.setPaused(true);
  input.setEnabled(false);
  pauseButton.textContent = "Resume";
  pauseButton.setAttribute("aria-pressed", "true");
  announce("Round paused because the page was hidden.");
});

window.addEventListener("beforeunload", () => {
  loop.stop();
  input.destroy();
  void audio.destroy();
});
